/**
 * Review › "Adjust on the video" — open/close, and the journal lifted back
 * into the wizard.
 *
 * The OWNERSHIP rule lives here. `OnboardingStudio` hands back the collapse of
 * the operator's OWN entries (never the seed, which is the wizard's own step
 * patch handed to it), and that collapse is stored apart from the steps'
 * writes, as `adjustLift`. Two things follow, and both were defects before:
 *
 *  - An Undo in the editor hands back a SMALLER patch, and because the lift is
 *    replaced rather than merged, the key genuinely goes — including the keys
 *    with no base value (`video.*`, the card's typography) that a merge-only
 *    design would have shipped at their undone number.
 *  - The seed is never re-dispatched, so a path the operator only looked at is
 *    never claimed, and a later re-read of the brand can still replace it.
 *
 * The panel's colour and face rows are folded into `colors` / `fonts` — the
 * wizard's own homes for them, and what `payload.ts` reads.
 */
import { useCallback, useEffect, useState, type Dispatch } from "react";
import type { EditPatch } from "../../../../../src/lib/clipEditState.ts";
import type { BrandSetupAction, BrandSetupState } from "../../../lib/brandSetup/state";

export interface AdjustController {
  adjusting: boolean;
  setAdjusting: (v: boolean) => void;
  onDesignChange: (design: EditPatch, steps: number) => void;
}

export function useAdjustLift(
  step: BrandSetupState["step"],
  dispatch: Dispatch<BrandSetupAction>,
): AdjustController {
  const [adjusting, setAdjusting] = useState(false);

  // Adjust is a Review-only surface: walking away from Review closes it, so
  // "Change → a step → Continue" can never land back on the editor.
  useEffect(() => {
    if (step !== "review") setAdjusting(false);
  }, [step]);

  const onDesignChange = useCallback(
    (design: EditPatch, steps: number): void => {
      dispatch({ type: "adjustLift", patch: design.layout, steps });
      const brand = design.brand;
      if (brand?.captionColor || brand?.captionAccent) {
        dispatch({
          type: "colors",
          colors: {
            ...(brand.captionColor ? { ink: brand.captionColor } : {}),
            ...(brand.captionAccent ? { accent: brand.captionAccent } : {}),
          },
        });
      }
      // One role in the payload (`fontRoles.caption` and `.speaker` are both
      // Body), so the card's face and the caption's face are the same answer —
      // showing that immediately beats shipping the surprise at render.
      const body = brand?.captionFont || brand?.speakerFont;
      if (body) dispatch({ type: "fonts", fonts: { body } });
    },
    [dispatch],
  );

  return { adjusting, setAdjusting, onDesignChange };
}
